import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Lock,
  Unlock,
  Search,
  RefreshCw,
  Trash2,
  Key,
  CheckCircle,
  XCircle,
  User,
  Bot,
  Settings,
  BarChart3,
  Target,
  List,
  DollarSign,
  ShieldCheck,
  Code,
  BookOpen,
  BookMarked,
  Shield,
  TrendingUp,
  AlertCircle,
  Download,
  FileText,
  Building2,
  PackageCheck,
  Store,
  Database,
  ShieldAlert,
  Award,
  HelpCircle,
  AlertTriangle,
  Link2,
  Zap,
  ClipboardList,
  Activity,
  Network,
  Palette,
  Sparkles,
  RotateCcw,
  Trash,
  Tag as TagIcon,
  Cpu,
  Eye,
  Fingerprint,
  Globe,
  Plug,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
import ExternalIntegrationsGate from '../components/Integrations/ExternalIntegrationsGate';
import { IntegrationsContent } from './Integrations';
import { useExternalIntegrations } from '../hooks/useExternalIntegrations';
import AIProviders from '../components/Admin/AIProviders';
import ModelPricing from '../components/Admin/ModelPricing';
import SOPPipelineModels from '../components/Admin/SOPPipelineModels';
import CouncilSettings from '../components/Admin/CouncilSettings';
import ClientTagManagement from '../components/Admin/ClientTagManagement';
import UsageDashboard from '../components/Admin/UsageDashboard';
import SSOConfiguration from '../components/Admin/SSOConfiguration';
import RoleManagement from '../components/Admin/RoleManagement';
import APIKeysConfig from '../components/Admin/APIKeysConfig';
import ProcessMetadata from '../components/Admin/ProcessMetadata';
import DeletedProjectsAdmin from '../components/Admin/DeletedProjectsAdmin';
import RiskAppetiteConfig from '../components/RCSA/RiskAppetiteConfig';
import RiskTaxonomyManagement from '../components/Admin/RiskTaxonomyManagement';
import ROIDashboard from '../components/Admin/ROIDashboard';
import AuditDashboard from './AuditDashboard';
import CodeStatistics from '../components/Admin/CodeStatistics';
import WorkflowToolsAdmin from '../components/Admin/WorkflowToolsAdmin';
import OrgStandardsLibraryAdmin from '../components/Admin/OrgStandardsLibraryAdmin';
import Pagination from '../components/Common/Pagination';
import UserCreateModal from '../components/Admin/UserCreateModal';
import LLMSelector from '../components/LLMSelector';
import TerminologyTemplateUpload from '../components/Terminology/TerminologyTemplateUpload';
import { FrameworkList, FrameworkStandard } from '../components/Framework/FrameworkList';
import { FrameworkDetailModal } from '../components/Framework/FrameworkDetailModal';
import TrustPortalLayout from '../components/TrustPortal/TrustPortalLayout';
import { useProject } from '../contexts/ProjectContext';
import TenantManagement from './TenantManagement';
import SecurityScanDashboard from '../components/Admin/SecurityScanDashboard';
import EvidenceHarvesting from './EvidenceHarvesting';
import ComplianceDrift from './ComplianceDrift';
import ControlMappingPage from './ControlMapping';
import AIAssistantPage from './AIAssistant';
import ChangeImpactPage from './ChangeImpact';
import QuestionnaireAutofillPage from './QuestionnaireAutofill';
import VendorManagement from './VendorManagement';
import AssetInventory from './AssetInventory';
import VulnerabilityDashboard from './VulnerabilityDashboard';
import VulnerabilityDetail from './VulnerabilityDetail';
import IpWhitelistManagement from '../components/Admin/IpWhitelistManagement';
import SystemSecuritySettings from '../components/SystemSecuritySettings';
import SecurityDocumentRepository from '../components/SecurityDocuments/SecurityDocumentRepository';
import PrivilegedAccessReports from '../components/Admin/PrivilegedAccessReports';
import TenantIsolationCompliance from '../components/Admin/TenantIsolationCompliance';
import AIActivityLog from '../components/Admin/AIActivityLog';
import ClientBrandingAdmin from '../components/Admin/ClientBrandingAdmin';
import ClientTagBrandingAdmin from '../components/Admin/ClientTagBrandingAdmin';
import ClientSopFormatsAdmin from '../components/Admin/ClientSopFormatsAdmin';
import SmartStartPipelineAdmin from './SmartStartPipelineAdmin';
import VerificationFeedbackAdmin from './VerificationFeedbackAdmin';
import CompanyBrain from './CompanyBrain';
import ComplianceDashboard from './ComplianceDashboard';
import VerificationDashboard from './VerificationDashboard';
import VerificationConfig from './VerificationConfig';
import AccuracyTestSuite from './AccuracyTestSuite';
import NewTenantWizard from '../components/Admin/NewTenantWizard';
import AuditReadinessWizard from '../components/Admin/AuditReadinessWizard';
import AIModelMigrationWizard from '../components/Admin/AIModelMigrationWizard';
import api from '../api';
import toast from 'react-hot-toast';

interface UserData {
  id: number;
  email: string;
  name: string;
  role: 'USER' | 'ADMIN' | 'MASTER' | 'SUPER_ADMIN';
  is_active: boolean;
  last_login: string | null;
  login_attempts: number;
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'LOCKED';
  isLocked: boolean;
  mfa_enabled: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  deletedBy?: { id: number; name: string; email: string } | null;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  lockedUsers: number;
  roleDistribution: {
    USER: number;
    ADMIN: number;
    MASTER: number;
  };
  recentLogins: {
    id: number;
    email: string;
    name: string;
    lastLogin: string;
  }[];
}

const Admin: React.FC = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const { currentProject } = useProject();
  const [users, setUsers] = useState<UserData[]>([]);
  // activeTab — only Risk Settings, Workflow Tools, Proof Points remain at top level
  // users-roles, tenant-management, ai-config, audit-logs, verification-accuracy all fold into Security Center
  const [activeTab, setActiveTab] = useState<'security-code' | 'risk-settings' | 'workflow-tools' | 'proof-points'>('security-code');
  const [proofPointsSubTab, setProofPointsSubTab] = useState<'roi' | 'code-statistics' | 'faqs'>('roi');
  const [deletedUsers, setDeletedUsers] = useState<UserData[]>([]);
  const [deletedUsersLoading, setDeletedUsersLoading] = useState(false);
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  // ── Security Center — 5 Mission Centers ─────────────────────────────
  type SecurityMissionCenter = 'identity-setup' | 'threat-command' | 'compliance-trust' | 'ai-ops-lab' | 'audit-monitoring';
  const [securityMissionCenter, setSecurityMissionCenter] = useState<SecurityMissionCenter>('identity-setup');

  // MC 1 — Identity & Setup
  const [identitySubTab, setIdentitySubTab] = useState<'users' | 'roles' | 'deleted-users' | 'tenant-management' | 'sso-logins' | 'ip-whitelist' | 'system-security' | 'integrations'>('users');
  // users sub-tab kept from old usersRolesSubTab — alias for compat
  const usersRolesSubTab = identitySubTab as any;
  const setUsersRolesSubTab = (v: string) => setIdentitySubTab(v as any);
  // Stemmler-only Integrations tab under Identity & Setup (firm Slack/Zoho + org Outlook)
  const { allowed: integrationsAllowed } = useExternalIntegrations();

  // MC 2 — Threat Command
  const [threatSubTab, setThreatSubTab] = useState<'vulnerability-management' | 'security-scan'>('vulnerability-management');
  const [selectedVulnerabilityId, setSelectedVulnerabilityId] = useState<number | null>(null);

  // MC 3 — Compliance & Trust
  const [complianceSubTab, setComplianceSubTab] = useState<'evidence-collection' | 'vendor-management' | 'asset-inventory' | 'security-documents' | 'trust-portal'>('evidence-collection');
  // Evidence Collection sub-sub-tabs
  const [evidenceCollectionSubTab, setEvidenceCollectionSubTab] = useState<'evidence-harvesting' | 'compliance-drift' | 'control-mapping' | 'ai-assistant' | 'change-impact' | 'questionnaire-autofill'>('evidence-harvesting');

  // MC 4 — AI Operations Lab (absorbs ai-config + verification-accuracy)
  // Top-level split: AI Configuration | Verification & Accuracy
  const [aiOpsMissionTab, setAiOpsMissionTab] = useState<'ai-config' | 'verification-accuracy'>('ai-config');
  const [aiOpsSubTab, setAiOpsSubTab] = useState<'providers' | 'api-keys' | 'llm-selector' | 'model-pricing' | 'client-tags' | 'usage-dashboard'>('providers');
  const [accuracyLabSubTab, setAccuracyLabSubTab] = useState<'dashboard' | 'config' | 'accuracy-test-suite'>('dashboard');
  // Legacy aliases for old ai-config state
  const aiConfigSubTab = aiOpsSubTab as any;
  const setAiConfigSubTab = (v: string) => setAiOpsSubTab(v as any);
  // Legacy alias for old verification-accuracy state
  const verificationSubTab = accuracyLabSubTab;
  const setVerificationSubTab = setAccuracyLabSubTab;

  // MC 5 — Audit & Monitoring (absorbs audit-logs tab)
  const [auditMonSubTab, setAuditMonSubTab] = useState<'audit-dashboard' | 'deleted-projects' | 'privileged-access' | 'tenant-isolation' | 'ai-activity' | 'ai-compliance' | 'verification-feedback'>('audit-dashboard');
  // Legacy alias for old audit-logs state
  const auditLogsSubTab = auditMonSubTab;
  const setAuditLogsSubTab = (v: string) => setAuditMonSubTab(v as any);

  // navigateToSecurityCenter — canonical deep-link router for jarvis-admin-navigate events.
  // Accepts any legacy top-tab ID or any sub/sub-sub-tab string and routes to the
  // correct Mission Center + sub-tab inside Security Center.
  const navigateToSecurityCenter = (tab: string) => {
    setActiveTab('security-code');
    // ── Identity & Setup ──────────────────────────────────────────────
    if (tab === 'users-roles' || tab === 'users') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('users'); }
    else if (tab === 'roles' || tab === 'permissions') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('roles'); }
    else if (tab === 'deleted-users') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('deleted-users'); }
    else if (tab === 'tenant-management') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('tenant-management'); }
    else if (tab === 'sso-logins') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('sso-logins'); }
    else if (tab === 'ip-whitelist') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('ip-whitelist'); }
    else if (tab === 'system-security') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('system-security'); }
    else if (tab === 'integrations') { setSecurityMissionCenter('identity-setup'); setIdentitySubTab('integrations'); }
    // ── Threat Command ────────────────────────────────────────────────
    else if (tab === 'vulnerability-management') { setSecurityMissionCenter('threat-command'); setThreatSubTab('vulnerability-management'); setSelectedVulnerabilityId(null); }
    else if (tab === 'security-scan') { setSecurityMissionCenter('threat-command'); setThreatSubTab('security-scan'); }
    // ── Compliance & Trust ────────────────────────────────────────────
    else if (tab === 'evidence-collection') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); }
    else if (tab === 'evidence-harvesting') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); setEvidenceCollectionSubTab('evidence-harvesting'); }
    else if (tab === 'compliance-drift') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); setEvidenceCollectionSubTab('compliance-drift'); }
    else if (tab === 'control-mapping') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); setEvidenceCollectionSubTab('control-mapping'); }
    else if (tab === 'ai-assistant') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); setEvidenceCollectionSubTab('ai-assistant'); }
    else if (tab === 'change-impact') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); setEvidenceCollectionSubTab('change-impact'); }
    else if (tab === 'questionnaire-autofill') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('evidence-collection'); setEvidenceCollectionSubTab('questionnaire-autofill'); }
    else if (tab === 'vendor-management') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('vendor-management'); }
    else if (tab === 'asset-inventory') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('asset-inventory'); }
    else if (tab === 'security-documents') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('security-documents'); }
    else if (tab === 'trust-portal') { setSecurityMissionCenter('compliance-trust'); setComplianceSubTab('trust-portal'); }
    // ── AI Operations Lab (absorbs old ai-config + verification-accuracy) ──
    else if (tab === 'ai-config' || tab === 'providers') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('ai-config'); setAiOpsSubTab('providers'); }
    else if (tab === 'api-keys') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('ai-config'); setAiOpsSubTab('api-keys'); }
    else if (tab === 'llm-selector' || tab === 'council-settings') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('ai-config'); setAiOpsSubTab('llm-selector'); }
    else if (tab === 'model-pricing') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('ai-config'); setAiOpsSubTab('model-pricing'); }
    else if (tab === 'client-tags') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('ai-config'); setAiOpsSubTab('client-tags'); }
    else if (tab === 'usage-dashboard') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('ai-config'); setAiOpsSubTab('usage-dashboard'); }
    else if (tab === 'accuracy-lab' || tab === 'verification-accuracy') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('verification-accuracy'); setAccuracyLabSubTab('dashboard'); }
    else if (tab === 'verification-dashboard') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('verification-accuracy'); setAccuracyLabSubTab('dashboard'); }
    else if (tab === 'verification-config') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('verification-accuracy'); setAccuracyLabSubTab('config'); }
    else if (tab === 'accuracy-test-suite') { setSecurityMissionCenter('ai-ops-lab'); setAiOpsMissionTab('verification-accuracy'); setAccuracyLabSubTab('accuracy-test-suite'); }
    // ── Audit & Monitoring (absorbs old audit-logs tab) ───────────────
    else if (tab === 'audit-logs' || tab === 'audit-dashboard') { setSecurityMissionCenter('audit-monitoring'); setAuditMonSubTab('audit-dashboard'); }
    else if (tab === 'deleted-projects') { setSecurityMissionCenter('audit-monitoring'); setAuditMonSubTab('deleted-projects'); }
    else if (tab === 'privileged-access') { setSecurityMissionCenter('audit-monitoring'); setAuditMonSubTab('privileged-access'); }
    else if (tab === 'tenant-isolation') { setSecurityMissionCenter('audit-monitoring'); setAuditMonSubTab('tenant-isolation'); }
    else if (tab === 'ai-activity') { setSecurityMissionCenter('audit-monitoring'); setAuditMonSubTab('ai-activity'); }
    else if (tab === 'verification-feedback') { setSecurityMissionCenter('audit-monitoring'); setAuditMonSubTab('verification-feedback'); }
  };
  // Legacy alias — existing code that calls setSecurityCodeSubTab keeps working
  const setSecurityCodeSubTab = navigateToSecurityCenter;

  // ── Guided Wizard open states ─────────────────────────────────────
  const [showNewTenantWizard, setShowNewTenantWizard] = useState(false);
  const [showAuditReadinessWizard, setShowAuditReadinessWizard] = useState(false);
  const [showAIModelMigrationWizard, setShowAIModelMigrationWizard] = useState(false);

  const [riskSettingsSubTab, setRiskSettingsSubTab] = useState<'process-metadata' | 'definitions-acronyms' | 'risk-appetite' | 'risk-taxonomy' | 'regulatory-frameworks'>('process-metadata');
  const [workflowToolsSubTab, setWorkflowToolsSubTab] = useState<'tsg-bios' | 'workflow-automation' | 'sop-formats' | 'org-standards' | 'client-branding' | 'smart-start-admin'>('tsg-bios');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const itemsPerPage = 10;
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Framework-specific state
  const [frameworks, setFrameworks] = useState<FrameworkStandard[]>([]);
  const [frameworkLoading, setFrameworkLoading] = useState(false);
  const [frameworkError, setFrameworkError] = useState<string | null>(null);
  const [selectedFramework, setSelectedFramework] = useState<FrameworkStandard | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [frameworkStats, setFrameworkStats] = useState({
    totalFrameworks: 0,
    totalStandards: 0,
    frameworkTypes: 0
  });


  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { isActive: statusFilter })
      });

      const response = await api.get(`/api/admin/users?${params}`);

      const data = response.data;
      setUsers(data.users || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalUsers(data.pagination?.totalRecords || 0);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  // Fetch user statistics
  const fetchStats = async () => {
    try {
      const response = await api.get('/api/admin/statistics');
      setStats(response.data);
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchDeletedUsers = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setDeletedUsersLoading(true);
      const response = await api.get('/api/admin/users/deleted');
      setDeletedUsers(response.data.users || []);
    } catch (error: any) {
      console.error('Error fetching deleted users:', error);
    } finally {
      setDeletedUsersLoading(false);
    }
  }, [isSuperAdmin]);

  // Load frameworks
  const loadFrameworks = async () => {
    try {
      setFrameworkLoading(true);
      setFrameworkError(null);
      const response = await api.get('/api/framework-standards', {
        params: { limit: 2000 }
      });

      if (response.data.success) {
        setFrameworks(response.data.data);
      }
    } catch (err: any) {
      console.error('Error loading frameworks:', err);
      setFrameworkError(err.response?.data?.message || 'Failed to load frameworks');
    } finally {
      setFrameworkLoading(false);
    }
  };

  const loadFrameworkStats = async () => {
    try {
      const response = await api.get('/api/framework-standards/frameworks');
      if (response.data.success) {
        const frameworkData = response.data.data;
        const totalStandards = frameworkData.reduce((sum: number, fw: any) => sum + parseInt(fw.standard_count), 0);

        setFrameworkStats({
          totalFrameworks: frameworkData.length,
          totalStandards: totalStandards,
          frameworkTypes: frameworkData.length
        });
      }
    } catch (err) {
      console.error('Error loading framework stats:', err);
    }
  };

  const handleSelectFramework = (framework: FrameworkStandard) => {
    setSelectedFramework(framework);
    setShowDetailModal(true);
  };

  const handleLoadControls = async (frameworkId: number) => {
    try {
      const response = await api.get('/api/control-standards', {
        params: { framework_standard_id: frameworkId }
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error('Error loading controls:', err);
      return [];
    }
  };

  const handleLoadCitations = async (frameworkId: number) => {
    try {
      const response = await api.get('/api/compliance-citations', {
        params: {
          framework_standard_id: frameworkId,
          limit: 100
        }
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error('Error loading citations:', err);
      return [];
    }
  };

  const handleExportFrameworks = () => {
    const headers = ['Framework', 'Standard ID', 'Standard Name', 'Category', 'Description'];
    const rows = frameworks.map(fw => [
      fw.framework_name,
      fw.standard_id,
      fw.standard_name,
      fw.category || '',
      fw.description || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `framework-standards-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Frameworks exported successfully');
  };

  // Deep-links (?tab=integrations, ?tab=branding, …). Must re-run when the
  // query string changes: /app/admin?tab=integrations (and jarvis-admin-navigate)
  // can land while Admin is already mounted, so a mount-only effect would leave
  // the user on whatever sub-tab they were on.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const frameworkParam = searchParams.get('framework');

    if (tabParam) {
      // Standalone tabs that still exist at the top level
      if (['risk-settings', 'workflow-tools', 'proof-points'].includes(tabParam)) {
        setActiveTab(tabParam as typeof activeTab);
      } else if (tabParam === 'regulatory-frameworks' || tabParam === 'process-metadata' || tabParam === 'risk-appetite' || tabParam === 'risk-taxonomy') {
        setActiveTab('risk-settings');
        setRiskSettingsSubTab(tabParam as any);
      } else if (tabParam === 'roi-dashboard' || tabParam === 'code-statistics') {
        setActiveTab('proof-points');
      } else if (tabParam === 'branding' || tabParam === 'client-branding') {
        // Deep-link from the BD New Engagement modal's "Add now →" amber
        // banner. Lands the user directly in the per-client-tag branding
        // editor; ClientTagBrandingAdmin reads its own ?tag=<id> param to
        // auto-expand the right row.
        setActiveTab('workflow-tools');
        setWorkflowToolsSubTab('client-branding');
      } else if (['tsg-bios', 'workflow-automation', 'sop-formats', 'org-standards', 'smart-start-admin'].includes(tabParam)) {
        // Direct deep-links to other Workflow Tools sub-tabs.
        setActiveTab('workflow-tools');
        setWorkflowToolsSubTab(tabParam as typeof workflowToolsSubTab);
      } else {
        // Everything else (users-roles, ai-config, audit-logs, audit-dashboard,
        // vulnerability-management, deleted-projects, sso-config, integrations, etc.)
        // routes through navigateToSecurityCenter which handles all consolidated tabs.
        navigateToSecurityCenter(tabParam);
      }
    }

    // If framework parameter is provided, we'll handle it after frameworks are loaded
    if (frameworkParam && tabParam === 'regulatory-frameworks') {
      // Store it temporarily to use after frameworks load
      sessionStorage.setItem('pendingFrameworkHighlight', frameworkParam);
    }
  }, [searchParams]);

  // Handle framework highlighting from URL after frameworks are loaded
  useEffect(() => {
    if (activeTab === 'risk-settings' && riskSettingsSubTab === 'regulatory-frameworks' && frameworks.length > 0) {
      const pendingFramework = sessionStorage.getItem('pendingFrameworkHighlight');
      if (pendingFramework) {
        // Find the framework that matches the name
        const framework = frameworks.find(f =>
          f.framework_name.toLowerCase() === pendingFramework.toLowerCase() ||
          f.standard_id.toLowerCase().includes(pendingFramework.toLowerCase())
        );

        if (framework) {
          // Auto-open the detail modal for this framework
          setSelectedFramework(framework);
          setShowDetailModal(true);
        }

        // Clear the pending highlight
        sessionStorage.removeItem('pendingFrameworkHighlight');
      }
    }
  }, [activeTab, riskSettingsSubTab, frameworks]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers]);

  useEffect(() => {
    if (usersRolesSubTab === 'deleted-users') {
      fetchDeletedUsers();
    }
  }, [usersRolesSubTab, fetchDeletedUsers]);

  useEffect(() => {
    if (activeTab === 'risk-settings' && riskSettingsSubTab === 'regulatory-frameworks') {
      loadFrameworks();
      loadFrameworkStats();
    }
  }, [activeTab, riskSettingsSubTab]);

  // ── Listener for Help panel "related link" navigation ──────────────
  // The HelpPanel component dispatches a `jarvis-admin-navigate` custom
  // event on window when a related-link button is clicked. We listen
  // here and update the Admin state accordingly so the user lands on
  // the target screen without having to click through the tab bar.
  // Payload fields are all optional so a link can target any level
  // (top tab, sub-tab, or evidence-collection sub-sub-tab).
  useEffect(() => {
    const handleAdminNavigate = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const { targetTopTab, targetSubTab, targetSubSubTab } = detail;

      // Old top-tab IDs that were consolidated into Security Center get
      // routed through navigateToSecurityCenter so Mission Center state is
      // set correctly. Remaining standalone tabs (risk-settings, etc.) set
      // activeTab directly.
      const consolidatedTabs = new Set([
        'users-roles', 'tenant-management', 'ai-config', 'audit-logs', 'verification-accuracy',
      ]);
      if (targetTopTab) {
        if (consolidatedTabs.has(targetTopTab)) {
          navigateToSecurityCenter(targetTopTab);
        } else {
          setActiveTab(targetTopTab as typeof activeTab);
        }
      }
      if (targetSubTab) {
        navigateToSecurityCenter(targetSubTab);
      }
      if (targetSubSubTab) {
        if (!targetSubTab) navigateToSecurityCenter('evidence-collection');
        setEvidenceCollectionSubTab(targetSubSubTab as typeof evidenceCollectionSubTab);
      }
    };

    window.addEventListener('jarvis-admin-navigate', handleAdminNavigate);
    return () => window.removeEventListener('jarvis-admin-navigate', handleAdminNavigate);
  }, []);


  const handleUserAction = async (userId: number, action: string, data?: any) => {
    try {
      setActionLoading(userId);
      let response;

      switch (action) {
        case 'toggle-status':
          response = await api.post(`/api/admin/users/${userId}/toggle-status`);
          break;
        case 'unlock':
          response = await api.post(`/api/admin/users/${userId}/unlock`);
          break;
        case 'reset-password':
          response = await api.post(`/api/admin/users/${userId}/reset-password`, { generateRandom: true });
          break;
        case 'delete': {
          const user = users.find(u => u.id === userId);
          const isSuperAdminTarget = user?.role === 'SUPER_ADMIN';

          const confirmMessage = isSuperAdminTarget
            ? `Are you sure you want to delete Super Admin "${user?.name || 'Unknown'}" (${user?.email || 'Unknown'})?

          This will:
          - Block the user from logging in
          - Move them to the Deleted Users tab
          - Preserve their audit trail

          A Super Admin can restore the account later.

          Type "DELETE" to confirm:`
            : `Are you sure you want to delete user "${user?.name || 'Unknown'}" (${user?.email || 'Unknown'})?

          This action cannot be undone and will:
          - Permanently remove the user account
          - Remove access to all projects and documents
          - Clear all associated permissions

          Type "DELETE" to confirm:`;

          const confirmation = prompt(confirmMessage);
          if (confirmation !== 'DELETE') {
            return;
          }

          response = await api.delete(`/api/admin/users/${userId}`);
          break;
        }
        case 'restore': {
          const user = deletedUsers.find(u => u.id === userId);
          if (!confirm(`Restore user "${user?.name || 'Unknown'}" (${user?.email || 'Unknown'})?\n\nThey will be able to log in again with their previous credentials.`)) {
            return;
          }
          response = await api.post(`/api/admin/users/${userId}/restore`);
          break;
        }
        default:
          return;
      }

      const result = response.data;

      if (action === 'reset-password' && result.temporaryPassword) {
        const tempPasswordAlert = `Password reset successfully!

          Temporary password: ${result.temporaryPassword}

          Please share this with the user securely and ask them to change it on first login.`;

        // Copy to clipboard if possible
        if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(result.temporaryPassword);
            toast.success('Password reset! Temporary password copied to clipboard.');
          } catch {
            alert(tempPasswordAlert);
          }
        } else {
          alert(tempPasswordAlert);
        }
      } else if (action === 'delete') {
        toast.success(`User "${result.deletedUser?.name || 'Unknown'}" has been deleted successfully`);
      } else if (action === 'restore') {
        toast.success(`User "${result.user?.name || 'Unknown'}" has been restored successfully`);
      } else {
        toast.success('Action completed successfully');
      }

      // Refresh data
      fetchUsers();
      fetchStats();
      if (action === 'delete' || action === 'restore') {
        fetchDeletedUsers();
      }

    } catch (error: any) {
      console.error('Error performing action:', error);

      // ✅ ENHANCED ERROR HANDLING
      const errorData = error.response?.data;
      let errorMessage = 'An unexpected error occurred';

      if (errorData) {
        if (errorData.error && errorData.message) {
          errorMessage = `${errorData.error}: ${errorData.message}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }

        if (errorData.projectCount || errorData.documentCount) {
          const details = [];
          if (errorData.projectCount) details.push(`${errorData.projectCount} projects`);
          if (errorData.documentCount) details.push(`${errorData.documentCount} documents`);

          errorMessage += `\n\nUser has: ${details.join(', ')}`;

          const offerTransfer = confirm(`${errorMessage}\n\nWould you like to transfer these items to another user first?`);
          if (offerTransfer) {
            return;
          }
        }
      }

      toast.error(errorMessage);

      // ✅ SPECIFIC ACTION FOR AUTH ERRORS
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('You do not have permission to perform this action');

        // Refresh user info in case role changed
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'bg-red-100 text-red-800';
      case 'ADMIN': return 'bg-blue-100 text-blue-800';
      case 'CONSULTANT': return 'bg-green-100 text-green-800';
      case 'READ_ONLY': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (user: UserData) => {
    if (!user.is_active) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    if (user.isLocked) {
      return <Lock className="w-4 h-4 text-yellow-500" />;
    }
    return <CheckCircle className="w-4 h-4 text-green-500" />;
  };

  // Users & Roles, Tenant Management, AI Config, Audit/Logs, Verification & Accuracy
  // all consolidated into Security Center Mission Centers per blueprint v2.0
  const tabs = [
    { id: 'security-code', label: 'Security Center', icon: Shield },
    { id: 'risk-settings', label: 'Risk Settings', icon: Target },
    { id: 'workflow-tools', label: 'Workflow Tools', icon: TrendingUp },
    { id: 'proof-points', label: 'Proof Points', icon: Award },
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  // Security check - only SUPER_ADMIN users can access admin
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentUser || (currentUser.role !== 'SUPER_ADMIN' && currentUser.role !== 'ADMIN')) {
    return <AccessDenied requiredRole="ADMIN" message={`Admin dashboard access is restricted to Admin and Super Admin users only. Current user: ${currentUser?.email}, Role: ${currentUser?.role}`} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Comprehensive system administration and user management</p>
              <div className="flex items-center mt-2 space-x-4">
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  System Status: Online
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="w-4 h-4 mr-1" />
                  {stats ? `${stats.totalUsers} Total Users` : 'Loading...'}
                </div>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:shadow-lg transition-shadow"
            >
              <UserPlus className="w-4 h-4" />
              <span>Create User</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <nav className="flex space-x-2 p-2 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          SECURITY CENTER — 5 Mission Centers (Blueprint v2.0)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'security-code' && (
        <div className="space-y-5">

          {/* ── Mission Center selector tiles ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {([
              {
                id: 'identity-setup' as const,
                label: 'Identity & Setup',
                desc: 'Getting people & orgs into the system securely',
                icon: Fingerprint,
                active: 'bg-blue-600',
                ring: 'ring-blue-300',
                idle: 'hover:border-blue-300 hover:bg-blue-50',
              },
              {
                id: 'threat-command' as const,
                label: 'Threat Command',
                desc: 'Finding & fixing vulnerabilities in real-time',
                icon: ShieldAlert,
                active: 'bg-red-600',
                ring: 'ring-red-300',
                idle: 'hover:border-red-300 hover:bg-red-50',
              },
              {
                id: 'compliance-trust' as const,
                label: 'Compliance & Trust',
                desc: 'Proving to auditors & clients the system is secure',
                icon: PackageCheck,
                active: 'bg-purple-600',
                ring: 'ring-purple-300',
                idle: 'hover:border-purple-300 hover:bg-purple-50',
              },
              {
                id: 'ai-ops-lab' as const,
                label: 'AI Operations Lab',
                desc: 'Managing cost, accuracy & reliability of AI agents',
                icon: Cpu,
                active: 'bg-amber-600',
                ring: 'ring-amber-300',
                idle: 'hover:border-amber-300 hover:bg-amber-50',
              },
              {
                id: 'audit-monitoring' as const,
                label: 'Audit & Monitoring',
                desc: 'Complete reviewable record of all security events',
                icon: Eye,
                active: 'bg-slate-700',
                ring: 'ring-slate-300',
                idle: 'hover:border-slate-300 hover:bg-slate-50',
              },
            ] as const).map(({ id, label, desc, icon: Icon, active, ring, idle }) => {
              const isActive = securityMissionCenter === id;
              return (
                <button
                  key={id}
                  onClick={() => setSecurityMissionCenter(id)}
                  className={`relative flex flex-col items-start p-4 rounded-xl border-2 text-left transition-all duration-150 ${
                    isActive
                      ? `${active} border-transparent text-white shadow-lg`
                      : `bg-white border-gray-200 text-gray-700 ${idle}`
                  }`}
                >
                  <div className={`p-2 rounded-lg mb-3 ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <span className={`font-semibold text-sm leading-tight mb-1 ${isActive ? 'text-white' : 'text-gray-900'}`}>{label}</span>
                  <span className={`text-xs leading-snug ${isActive ? 'text-white/70' : 'text-gray-500'}`}>{desc}</span>
                  {isActive && <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full opacity-80" />}
                </button>
              );
            })}
          </div>

          {/* ════════════════════════════════════════════════════════
              MC 1 — IDENTITY & SETUP
              Users, Roles, Tenant Mgmt, SSO, IP Whitelist,
              System Security Settings
          ════════════════════════════════════════════════════════ */}
          {securityMissionCenter === 'identity-setup' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <Fingerprint className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900">Identity & Setup</p>
                  <p className="text-xs text-blue-700 mt-0.5">Configure who can access Jarvis and how. Set up tenants, manage user roles, connect SSO providers, restrict access by IP, and manage system-wide security settings. Most of these are configured once during onboarding.</p>
                </div>
                {isSuperAdmin && (
                  <button onClick={() => setShowNewTenantWizard(true)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap">
                    <Building2 className="w-3.5 h-3.5" />Create Tenant
                  </button>
                )}
              </div>

              {/* Sub-tab bar */}
              <div className="bg-white rounded-lg border border-gray-200 p-2">
                <div className="flex flex-wrap gap-2">
                  {([
                    { id: 'users' as const, label: 'User Management', icon: Users },
                    { id: 'roles' as const, label: 'Roles & Permissions', icon: Settings },
                    { id: 'deleted-users' as const, label: 'Deleted Users', icon: Trash, superAdminOnly: true },
                    { id: 'tenant-management' as const, label: 'Tenant Management', icon: Building2, superAdminOnly: true },
                    { id: 'sso-logins' as const, label: 'SSO & Logins', icon: Globe },
                    { id: 'ip-whitelist' as const, label: 'IP Whitelist', icon: Network },
                    { id: 'system-security' as const, label: 'System Security', icon: ShieldCheck, superAdminOnly: true },
                    { id: 'integrations' as const, label: 'Integrations', icon: Plug, integrationsOnly: true },
                  ] as const).filter(t => {
                    if ('superAdminOnly' in t && t.superAdminOnly && !isSuperAdmin) return false;
                    if ('integrationsOnly' in t && t.integrationsOnly && !integrationsAllowed) return false;
                    return true;
                  }).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setIdentitySubTab(id)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                        identitySubTab === id
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                      {id === 'system-security' && (
                        <span className="ml-1 text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-bold">SA</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* User Management */}
              {identitySubTab === 'users' && (
                <>
                  {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white p-6 rounded-lg shadow border"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Users</p><p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p></div><Users className="w-8 h-8 text-blue-600" /></div></div>
                      <div className="bg-white p-6 rounded-lg shadow border"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Active Users</p><p className="text-2xl font-bold text-green-600">{stats.activeUsers}</p></div><CheckCircle className="w-8 h-8 text-green-600" /></div></div>
                      <div className="bg-white p-6 rounded-lg shadow border"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Inactive Users</p><p className="text-2xl font-bold text-gray-600">{stats.inactiveUsers}</p></div><XCircle className="w-8 h-8 text-gray-600" /></div></div>
                      <div className="bg-white p-6 rounded-lg shadow border"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Locked Users</p><p className="text-2xl font-bold text-yellow-600">{stats.lockedUsers}</p></div><Lock className="w-8 h-8 text-yellow-600" /></div></div>
                    </div>
                  )}
                  <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><input type="text" placeholder="Search users..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
                      <select className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}><option value="">All Roles</option><option value="SUPER_ADMIN">Super Admin</option><option value="ADMIN">Admin</option><option value="CONSULTANT">Consultant</option><option value="READ_ONLY">Read Only</option></select>
                      <select className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="">All Status</option><option value="true">Active</option><option value="false">Inactive</option></select>
                      <button onClick={() => { setSearchTerm(''); setRoleFilter(''); setStatusFilter(''); setCurrentPage(1); }} className="px-4 py-2 border rounded-md hover:bg-gray-50 flex items-center gap-2"><RefreshCw className="w-4 h-4" />Clear Filters</button>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow border overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MFA</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {loading ? (
                            <tr><td colSpan={7} className="px-6 py-4"><div className="flex items-center justify-center h-32"><RefreshCw className="w-8 h-8 animate-spin text-blue-500" /></div></td></tr>
                          ) : users.length === 0 ? (
                            <tr><td colSpan={7} className="px-6 py-4 text-center text-gray-500">No users found</td></tr>
                          ) : (
                            users.map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4"><div className="flex items-center"><div className="flex-shrink-0 h-10 w-10"><div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center"><User className="w-5 h-5 text-gray-600" /></div></div><div className="ml-4"><div className="text-sm font-medium text-gray-900">{user.name}</div><div className="text-sm text-gray-500">{user.email}</div></div></div></td>
                                <td className="px-6 py-4"><span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>{user.role}</span></td>
                                <td className="px-6 py-4"><div className="flex items-center gap-2">{getStatusIcon(user)}<span className="text-sm text-gray-900">{user.accountStatus}</span></div></td>
                                <td className="px-6 py-4">{user.mfa_enabled ? (<span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"><Shield className="w-3 h-3 mr-1" />Enabled</span>) : (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') ? (<span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" />Required</span>) : (<span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Not Set</span>)}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.last_login)}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                  <div className="flex items-center gap-2">
                                    {user.isLocked && (<button onClick={() => handleUserAction(user.id, 'unlock')} disabled={actionLoading === user.id} className="text-yellow-600 hover:text-yellow-800 disabled:opacity-50" title="Unlock Account"><Unlock className="w-4 h-4" /></button>)}
                                    <button onClick={() => handleUserAction(user.id, 'toggle-status')} disabled={actionLoading === user.id} className={`${user.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} disabled:opacity-50`} title={user.is_active ? 'Deactivate' : 'Activate'}>{user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}</button>
                                    <button onClick={() => handleUserAction(user.id, 'reset-password')} disabled={actionLoading === user.id} className="text-blue-600 hover:text-blue-800 disabled:opacity-50" title="Reset Password"><Key className="w-4 h-4" /></button>
                                    {(() => { const isSelf = user.id === currentUser?.id; const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN'; const canDelete = !isSelf && isAdmin; if (!canDelete) return null; return (<button onClick={() => handleUserAction(user.id, 'delete')} disabled={actionLoading === user.id} className="text-red-600 hover:text-red-800 disabled:opacity-50" title="Delete User"><Trash2 className="w-4 h-4" /></button>); })()}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {totalUsers > 0 && (<Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalUsers} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} itemName="users" />)}
                  </div>
                </>
              )}

              {/* Roles & Permissions */}
              {identitySubTab === 'roles' && <RoleManagement />}

              {/* Deleted Users */}
              {identitySubTab === 'deleted-users' && isSuperAdmin && (
                <div className="bg-white rounded-lg shadow border">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div><h3 className="text-lg font-semibold text-gray-900">Deleted Users</h3><p className="text-sm text-gray-500 mt-1">Soft-deleted accounts. Restore to allow the user to log in again.</p></div>
                    <button onClick={fetchDeletedUsers} disabled={deletedUsersLoading} className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50" title="Refresh"><RefreshCw className={`w-4 h-4 ${deletedUsersLoading ? 'animate-spin' : ''}`} /><span>Refresh</span></button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted At</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted By</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {deletedUsersLoading ? (
                          <tr><td colSpan={6} className="px-6 py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin text-blue-500 mx-auto" /></td></tr>
                        ) : deletedUsers.length === 0 ? (
                          <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-500">No deleted users found</td></tr>
                        ) : deletedUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                            <td className="px-6 py-4"><span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>{user.role}</span></td>
                            <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.deleted_at || null)}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{user.deletedBy ? `${user.deletedBy.name} (${user.deletedBy.email})` : '—'}</td>
                            <td className="px-6 py-4"><button onClick={() => handleUserAction(user.id, 'restore')} disabled={actionLoading === user.id} className="flex items-center gap-1 text-green-600 hover:text-green-800 disabled:opacity-50 text-sm font-medium"><RotateCcw className="w-4 h-4" />Restore</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tenant Management */}
              {identitySubTab === 'tenant-management' && isSuperAdmin && <TenantManagement />}

              {/* SSO & Logins */}
              {identitySubTab === 'sso-logins' && <SSOConfiguration />}

              {/* IP Whitelist */}
              {identitySubTab === 'ip-whitelist' && <IpWhitelistManagement />}

              {/* System Security Settings — Super Admin only */}
              {identitySubTab === 'integrations' && integrationsAllowed && (
                <ExternalIntegrationsGate>
                  <IntegrationsContent />
                </ExternalIntegrationsGate>
              )}

              {identitySubTab === 'system-security' && isSuperAdmin && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    <ShieldCheck className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-900">System Security Settings <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">SUPER ADMIN ONLY</span></p>
                      <p className="text-xs text-red-700 mt-0.5">Platform-wide antivirus, data retention, security headers, file upload restrictions, compliance mode and session policy. Changes affect all users and tenants immediately.</p>
                    </div>
                  </div>
                  <SystemSecuritySettings />
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════
              MC 2 — THREAT COMMAND
              Security Scan · Vulnerability Management
              + Security Pulse sticky widget
          ════════════════════════════════════════════════════════ */}
          {securityMissionCenter === 'threat-command' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-900">Threat Command</p>
                  <p className="text-xs text-red-700 mt-0.5">Monitor your active threat surface. Run security scans, triage vulnerabilities, and check the health of scanning tools (Semgrep, Gitleaks, npm audit, Trivy). Start your daily security review here.</p>
                </div>
              </div>

              {/* Security Pulse — daily routine widget */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/10 rounded-lg"><ShieldAlert className="w-5 h-5 text-emerald-400" /></div>
                    <div>
                      <h3 className="font-bold text-base leading-none">Security Pulse</h3>
                      <p className="text-slate-400 text-xs mt-0.5">Daily security routine — ~5 min/day</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-emerald-300 text-xs font-medium">Live</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {[
                    'Review Critical & High items in the Global Alert Inbox',
                    'Check Compliance Score in Evidence Collection → Compliance Drift for drops',
                    'Confirm Tenant Isolation Live Report shows COMPLIANT',
                    'Confirm no new Critical/High CVEs in Security Scan → Findings',
                  ].map((item, i) => (
                    <label key={i} className="flex items-start gap-2 bg-white/5 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/10 transition-colors">
                      <input type="checkbox" className="mt-0.5 rounded accent-emerald-400" />
                      <span className="text-slate-200 text-xs leading-snug">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sub-tab bar */}
              <div className="bg-white rounded-lg border border-gray-200 p-2">
                <div className="flex gap-2">
                  {([
                    { id: 'vulnerability-management' as const, label: 'Vulnerability Management', icon: ShieldAlert },
                    { id: 'security-scan' as const, label: 'Security Scan', icon: ShieldCheck },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button key={id} onClick={() => { setThreatSubTab(id); if (id === 'vulnerability-management') setSelectedVulnerabilityId(null); }}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${threatSubTab === id ? 'bg-red-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                      <Icon className="w-4 h-4" /><span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {threatSubTab === 'vulnerability-management' && (
                selectedVulnerabilityId ? (
                  <VulnerabilityDetail vulnerabilityId={selectedVulnerabilityId} onBack={() => setSelectedVulnerabilityId(null)} />
                ) : (
                  <VulnerabilityDashboard onViewVulnerability={(id: number) => setSelectedVulnerabilityId(id)} />
                )
              )}
              {threatSubTab === 'security-scan' && <SecurityScanDashboard />}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════
              MC 3 — COMPLIANCE & TRUST
              Evidence Collection (6 sub-tabs) · Vendor · Asset ·
              Security Documents · Trust Portal
          ════════════════════════════════════════════════════════ */}
          {securityMissionCenter === 'compliance-trust' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                <PackageCheck className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-900">Compliance & Trust</p>
                  <p className="text-xs text-purple-700 mt-0.5">Manage everything an auditor or banking client needs to trust your platform. Harvest evidence from 5 live sources, monitor real-time compliance drift against OWASP/SOC2/ISO/PCI/NIST, map controls to frameworks, manage vendor risk, maintain your asset inventory and BYOD policy, and access the Trust Portal. Use the Audit Readiness Wizard for end-to-end SOC2/ISO prep.</p>
                </div>
                <button onClick={() => setShowAuditReadinessWizard(true)}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap">
                  <BookOpen className="w-3.5 h-3.5" />Prepare for Audit
                </button>
              </div>

              {/* Primary sub-tab bar */}
              <div className="bg-white rounded-lg border border-gray-200 p-2">
                <div className="flex flex-wrap gap-2">
                  {([
                    { id: 'evidence-collection' as const, label: 'Evidence Collection', icon: PackageCheck },
                    { id: 'vendor-management' as const, label: 'Vendor Management', icon: Store },
                    { id: 'asset-inventory' as const, label: 'Asset Inventory', icon: Database },
                    { id: 'security-documents' as const, label: 'Security Documents', icon: Lock },
                    { id: 'trust-portal' as const, label: 'Trust Portal', icon: BookOpen },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button key={id} onClick={() => setComplianceSubTab(id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${complianceSubTab === id ? 'bg-purple-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                      <Icon className="w-4 h-4" /><span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Evidence Collection — 6 sub-sub-tabs */}
              {complianceSubTab === 'evidence-collection' && (
                <div className="space-y-3">
                  <div className="bg-white rounded-lg border border-gray-200 p-1.5 flex flex-wrap gap-1">
                    {([
                      { id: 'evidence-harvesting' as const, label: 'Evidence Harvesting', icon: PackageCheck },
                      { id: 'compliance-drift' as const, label: 'Compliance Drift', icon: AlertTriangle },
                      { id: 'control-mapping' as const, label: 'Control Mapping', icon: Link2 },
                      { id: 'ai-assistant' as const, label: 'AI Assistant', icon: Bot },
                      { id: 'change-impact' as const, label: 'Change Impact', icon: Zap },
                      { id: 'questionnaire-autofill' as const, label: 'Auto-Fill Questionnaire', icon: ClipboardList },
                    ] as const).map(({ id, label, icon: Icon }) => (
                      <button key={id} onClick={() => setEvidenceCollectionSubTab(id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md font-medium text-sm transition-all duration-200 whitespace-nowrap ${evidenceCollectionSubTab === id ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                        <Icon className="w-4 h-4" /><span>{label}</span>
                      </button>
                    ))}
                  </div>
                  {evidenceCollectionSubTab === 'evidence-harvesting' && <EvidenceHarvesting />}
                  {evidenceCollectionSubTab === 'compliance-drift' && <ComplianceDrift />}
                  {evidenceCollectionSubTab === 'control-mapping' && <ControlMappingPage />}
                  {evidenceCollectionSubTab === 'ai-assistant' && <AIAssistantPage />}
                  {evidenceCollectionSubTab === 'change-impact' && <ChangeImpactPage />}
                  {evidenceCollectionSubTab === 'questionnaire-autofill' && <QuestionnaireAutofillPage />}
                </div>
              )}

              {complianceSubTab === 'vendor-management' && <VendorManagement />}
              {complianceSubTab === 'asset-inventory' && <AssetInventory />}
              {complianceSubTab === 'security-documents' && <SecurityDocumentRepository />}
              {complianceSubTab === 'trust-portal' && <TrustPortalLayout />}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════
              MC 4 — AI OPERATIONS LAB
              AI Providers · API Keys · LLM Selection · Model Pricing ·
              Client Tags · Usage & Cost · Accuracy Lab (3 sub-tabs)
          ════════════════════════════════════════════════════════ */}
          {securityMissionCenter === 'ai-ops-lab' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <Cpu className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">AI Operations Lab</p>
                  <p className="text-xs text-amber-700 mt-0.5">Monitor AI model performance, usage costs, and output quality. The Usage &amp; Cost dashboard flags anomalies and shows adoption heatmaps and per-client spend. The Accuracy Lab is your internal tool for debugging hallucinations, calibrating confidence thresholds, and validating model changes before they go live.</p>
                </div>
                {isSuperAdmin && (
                  <button onClick={() => setShowAIModelMigrationWizard(true)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap">
                    <Bot className="w-3.5 h-3.5" />Switch Model
                  </button>
                )}
              </div>

              {/* Top-level mission tabs: AI Configuration | Verification & Accuracy */}
              <div className="bg-white rounded-lg border border-gray-200 p-1.5 flex gap-1">
                <button
                  onClick={() => setAiOpsMissionTab('ai-config')}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-all duration-200 ${aiOpsMissionTab === 'ai-config' ? 'bg-amber-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <Settings className="w-4 h-4" /><span>AI Configuration</span>
                </button>
                <button
                  onClick={() => setAiOpsMissionTab('verification-accuracy')}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-all duration-200 ${aiOpsMissionTab === 'verification-accuracy' ? 'bg-amber-600 text-white shadow-sm' : 'text-amber-700 hover:bg-amber-50 border border-amber-200'}`}>
                  <Cpu className="w-4 h-4" /><span>Verification &amp; Accuracy</span>
                  <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold leading-none">Internal</span>
                </button>
              </div>

              {/* ── AI Configuration ── */}
              {aiOpsMissionTab === 'ai-config' && (
                <div className="space-y-4">
                  {/* AI Config sub-tab bar */}
                  <div className="bg-white rounded-lg border border-gray-200 p-2">
                    <div className="flex flex-wrap gap-2">
                      {([
                        { id: 'providers' as const, label: 'AI Providers', icon: Network },
                        { id: 'api-keys' as const, label: 'API Keys', icon: Key },
                        { id: 'llm-selector' as const, label: 'LLM Model Selection', icon: Settings },
                        { id: 'model-pricing' as const, label: 'Model Pricing', icon: DollarSign },
                        { id: 'client-tags' as const, label: 'Client Tags', icon: TagIcon },
                        { id: 'usage-dashboard' as const, label: 'Usage & Cost', icon: Activity },
                      ]).map(({ id, label, icon: Icon }) => (
                        <button key={id} onClick={() => setAiOpsSubTab(id)}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${aiOpsSubTab === id ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                          <Icon className="w-4 h-4" /><span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {aiOpsSubTab === 'providers' && <AIProviders />}
                  {aiOpsSubTab === 'api-keys' && <APIKeysConfig />}
                  {aiOpsSubTab === 'llm-selector' && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900 mb-2">LLM Model Selection</h2><p className="text-gray-600">Configure the default AI model used across the platform. Only Super Admins can change this — it affects all users.</p></div>
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg"><Bot className="w-6 h-6 text-white" /></div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Current AI Model Configuration</h3>
                            <div className="bg-white rounded-lg p-4 border border-gray-200"><LLMSelector showStatus={true} onModelChange={(_modelId, modelName) => { toast.success(`AI Model changed to ${modelName}`); }} /></div>
                            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start space-x-2"><Settings className="w-5 h-5 text-blue-600 mt-0.5" /><div><h4 className="font-semibold text-blue-900 text-sm mb-1">Model Selection Guidelines</h4><ul className="text-xs text-blue-700 space-y-1"><li>• <strong>Claude 4 Sonnet:</strong> Best overall for complex analysis and document processing</li><li>• <strong>Claude 3.5 Haiku:</strong> Fastest for quick queries and simple tasks</li><li>• <strong>Claude 3 Opus:</strong> Most capable for advanced reasoning</li><li>• <strong>GPT/Gemini:</strong> Alternative providers (requires API key configuration)</li></ul></div></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"><div className="flex items-start space-x-2"><ShieldCheck className="w-5 h-5 text-yellow-600 mt-0.5" /><div><h4 className="font-semibold text-yellow-900 text-sm mb-1">Important Notes</h4><ul className="text-xs text-yellow-700 space-y-1"><li>• Changes affect all users across the organization</li><li>• Ensure API keys are configured before selecting non-Anthropic models</li><li>• Different models may have different usage costs and rate limits</li></ul></div></div></div>
                      <div className="mt-6"><SOPPipelineModels /></div>
                      <div className="mt-6 pt-6 border-t border-gray-200"><CouncilSettings /></div>
                    </div>
                  )}
                  {aiOpsSubTab === 'model-pricing' && <ModelPricing />}
                  {aiOpsSubTab === 'client-tags' && <ClientTagManagement />}
                  {aiOpsSubTab === 'usage-dashboard' && <UsageDashboard />}
                </div>
              )}

              {/* ── Verification & Accuracy ── */}
              {aiOpsMissionTab === 'verification-accuracy' && (
                <div className="space-y-3">
                  <div className="bg-white rounded-lg border border-gray-200 p-1.5 flex gap-1">
                    {([
                      { id: 'dashboard' as const, label: 'Verification Dashboard', icon: ShieldCheck },
                      { id: 'config' as const, label: 'Verification Config', icon: Settings },
                      { id: 'accuracy-test-suite' as const, label: 'Accuracy Test Suite', icon: FileText },
                    ] as const).map(({ id, label, icon: Icon }) => (
                      <button key={id} onClick={() => setAccuracyLabSubTab(id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md font-medium text-sm transition-all duration-200 whitespace-nowrap ${accuracyLabSubTab === id ? 'bg-amber-600 text-white shadow-sm' : 'text-amber-700 hover:bg-amber-50'}`}>
                        <Icon className="w-4 h-4" /><span>{label}</span>
                      </button>
                    ))}
                  </div>
                  {accuracyLabSubTab === 'dashboard' && <VerificationDashboard />}
                  {accuracyLabSubTab === 'config' && <VerificationConfig />}
                  {accuracyLabSubTab === 'accuracy-test-suite' && <AccuracyTestSuite />}
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════
              MC 5 — AUDIT & MONITORING
              Audit Dashboard · Deleted Projects · Privileged Access ·
              Tenant Isolation · AI Activity Log
          ════════════════════════════════════════════════════════ */}
          {securityMissionCenter === 'audit-monitoring' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                <Eye className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Audit & Monitoring</p>
                  <p className="text-xs text-slate-600 mt-0.5">Full audit trail for the platform. Download purge certificates, monitor Super Admin privileged access, check tenant isolation compliance, and search AI activity logs with full prompt + response drill-down.</p>
                </div>
              </div>

              {/* Sub-tab bar */}
              <div className="bg-white rounded-lg border border-gray-200 p-2">
                <div className="flex flex-wrap gap-2">
                  {([
                    { id: 'audit-dashboard' as const, label: 'Audit Dashboard', icon: ShieldCheck, show: true },
                    { id: 'deleted-projects' as const, label: 'Deleted Projects', icon: Trash2, show: true },
                    { id: 'privileged-access' as const, label: 'Privileged Access', icon: Key, show: true },
                    { id: 'tenant-isolation' as const, label: 'Tenant Isolation', icon: Network, show: true },
                    { id: 'ai-activity' as const, label: 'AI Activity Log', icon: Bot, show: true },
                    { id: 'ai-compliance' as const, label: 'AI Compliance', icon: ShieldCheck, show: true },
                    { id: 'verification-feedback' as const, label: 'Verification Feedback', icon: ClipboardList, show: true },
                  ] as const).filter(t => t.show).map(({ id, label, icon: Icon }) => (
                    <button key={id} onClick={() => setAuditMonSubTab(id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${auditMonSubTab === id ? 'bg-slate-700 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                      <Icon className="w-4 h-4" /><span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {auditMonSubTab === 'audit-dashboard' && <AuditDashboard />}
              {auditMonSubTab === 'deleted-projects' && <DeletedProjectsAdmin />}
              {auditMonSubTab === 'privileged-access' && <PrivilegedAccessReports />}
              {auditMonSubTab === 'tenant-isolation' && <TenantIsolationCompliance />}
              {auditMonSubTab === 'ai-activity' && <AIActivityLog />}
              {auditMonSubTab === 'ai-compliance' && <ComplianceDashboard embedded />}
              {auditMonSubTab === 'verification-feedback' && <VerificationFeedbackAdmin />}
            </div>
          )}

        {/* ── Guided Wizards ── */}
        <NewTenantWizard isOpen={showNewTenantWizard} onClose={() => setShowNewTenantWizard(false)} />
        <AuditReadinessWizard isOpen={showAuditReadinessWizard} onClose={() => setShowAuditReadinessWizard(false)} />
        <AIModelMigrationWizard isOpen={showAIModelMigrationWizard} onClose={() => setShowAIModelMigrationWizard(false)} />
        </div>
      )}

      {/* Proof Points Tab */}
      {activeTab === 'proof-points' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2">
              <button
                onClick={() => setProofPointsSubTab('roi')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  proofPointsSubTab === 'roi'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>ROI</span>
              </button>
              <button
                onClick={() => setProofPointsSubTab('code-statistics')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  proofPointsSubTab === 'code-statistics'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Code className="w-4 h-4" />
                <span>Code Statistics</span>
              </button>
              <button
                onClick={() => setProofPointsSubTab('faqs')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  proofPointsSubTab === 'faqs'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <HelpCircle className="w-4 h-4" />
                <span>FAQs</span>
              </button>
            </div>
          </div>

          {proofPointsSubTab === 'roi' && <ROIDashboard />}
          {proofPointsSubTab === 'code-statistics' && <CodeStatistics />}
          {proofPointsSubTab === 'faqs' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <HelpCircle className="w-6 h-6 text-blue-500" />
                <h2 className="text-2xl font-bold text-gray-900">FAQs</h2>
              </div>
              <p className="text-gray-500">Frequently asked questions will appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* Risk Settings Tab */}
      {activeTab === 'risk-settings' && (
        <div className="space-y-6">
          {/* Sub-tabs for Risk Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setRiskSettingsSubTab('process-metadata')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'process-metadata'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Process Metadata</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('definitions-acronyms')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'definitions-acronyms'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Definitions & Acronyms</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('risk-appetite')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'risk-appetite'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Target className="w-4 h-4" />
                <span>Risk Appetite Config</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('risk-taxonomy')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'risk-taxonomy'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-4 h-4" />
                <span>Risk Taxonomy</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('regulatory-frameworks')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'regulatory-frameworks'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Regulatory Frameworks</span>
              </button>
            </div>
          </div>

          {riskSettingsSubTab === 'process-metadata' && <ProcessMetadata />}
          {riskSettingsSubTab === 'definitions-acronyms' && (
            <div className="space-y-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-purple-900 mb-2">
                  Terminology Templates
                </h3>
                <p className="text-purple-700 text-sm">
                  Upload terminology templates (PDF, Word, Excel) to automatically extract definitions for terminology matching during document analysis.
                  These definitions will be available across all projects in your organization.
                </p>
              </div>

              {currentProject ? (
                <TerminologyTemplateUpload projectId={currentProject.id} />
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    Please select a project from the Dashboard to upload terminology templates.
                  </p>
                </div>
              )}
            </div>
          )}
          {riskSettingsSubTab === 'risk-appetite' && <RiskAppetiteConfig />}
          {riskSettingsSubTab === 'risk-taxonomy' && <RiskTaxonomyManagement />}
          {riskSettingsSubTab === 'regulatory-frameworks' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-blue-900">{frameworkStats.totalStandards}</div>
                      <div className="text-blue-700 text-sm">Total Standards</div>
                    </div>
                    <BookOpen className="h-8 w-8 text-blue-400" />
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-purple-900">{frameworkStats.frameworkTypes}</div>
                      <div className="text-purple-700 text-sm">Framework Types</div>
                    </div>
                    <Shield className="h-8 w-8 text-purple-400" />
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-green-900">4</div>
                      <div className="text-green-700 text-sm">Industries Covered</div>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-400" />
                  </div>
                </div>
              </div>

              {/* Info Banner with Export */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-blue-900 mb-1">About Regulatory Frameworks</h3>
                  <p className="text-sm text-blue-800">
                    This section provides access to major regulatory frameworks including Basel Committee principles,
                    NIST CSF 2.0, CISA Cross-Sector CPGs, CRI Profile v2.1, ISO 27002 controls, and COSO Internal Control standards.
                    Click on any standard to view detailed requirements and related controls.
                  </p>
                </div>
                <button
                  onClick={handleExportFrameworks}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
              </div>

              {/* Error Display */}
              {frameworkError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800 mb-1">Error Loading Frameworks</h3>
                    <p className="text-sm text-red-700">{frameworkError}</p>
                    <button
                      onClick={loadFrameworks}
                      className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* Framework List */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <FrameworkList
                  frameworks={frameworks}
                  onSelectFramework={handleSelectFramework}
                  loading={frameworkLoading}
                />
              </div>

              {/* Framework Categories Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Basel Committee</h4>
                  <p className="text-sm text-blue-800">
                    11 principles for operational risk management in banking institutions
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">NIST Frameworks</h4>
                  <p className="text-sm text-purple-800">
                    NIST CSF 2.0 (34 standards) and SP 800-53 security controls
                  </p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2">CISA CPGs</h4>
                  <p className="text-sm text-red-800">
                    20 Cross-Sector Cybersecurity Performance Goals for critical infrastructure
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-2">CRI Profile v2.1</h4>
                  <p className="text-sm text-yellow-800">
                    1,214 standards replacing FFIEC CAT (retired August 2025) for financial services
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">ISO 27002</h4>
                  <p className="text-sm text-green-800">
                    Information security controls across organizational, technical, and physical domains
                  </p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-2">COSO Framework</h4>
                  <p className="text-sm text-orange-800">
                    5 components and 17 principles for internal control and enterprise risk management
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Workflow Tools Tab - Updated with sub-tabs */}
      {activeTab === 'workflow-tools' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2 flex-wrap">
              <button
                onClick={() => setWorkflowToolsSubTab('tsg-bios')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'tsg-bios'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>TSG Bios &amp; Case Studies</span>
              </button>
              <button
                onClick={() => setWorkflowToolsSubTab('workflow-automation')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'workflow-automation'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Workflow Automation Platforms</span>
              </button>
              <button
                onClick={() => setWorkflowToolsSubTab('sop-formats')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'sop-formats'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>SOP Formats</span>
              </button>
              <button
                onClick={() => setWorkflowToolsSubTab('org-standards')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'org-standards'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookMarked className="w-4 h-4" />
                <span>Standards Library</span>
              </button>
              <button
                onClick={() => setWorkflowToolsSubTab('client-branding')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'client-branding'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Palette className="w-4 h-4" />
                <span>Document Style Guides</span>
              </button>
              <button
                onClick={() => setWorkflowToolsSubTab('smart-start-admin')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'smart-start-admin'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                <span>Smart Start Admin</span>
              </button>
            </div>
          </div>

          {workflowToolsSubTab === 'tsg-bios' && <CompanyBrain />}
          {workflowToolsSubTab === 'workflow-automation' && <WorkflowToolsAdmin />}
          {workflowToolsSubTab === 'client-branding' && (
            <div className="space-y-6">
              {/* Primary path: per-client-tag branding (preferred). Edits here
                  flow to every engagement, project, and export under the tag. */}
              <ClientTagBrandingAdmin />
              {/* Legacy path: per-project brandings. Still editable for
                  historical projects that pre-date the client-tag system. */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <details>
                  <summary className="cursor-pointer text-sm font-medium text-gray-700">
                    Project-scoped branding (legacy) — kept editable for historical projects
                  </summary>
                  <div className="mt-4">
                    <ClientBrandingAdmin />
                  </div>
                </details>
              </div>
            </div>
          )}
          {workflowToolsSubTab === 'smart-start-admin' && <SmartStartPipelineAdmin embedded />}
          {workflowToolsSubTab === 'org-standards' && <OrgStandardsLibraryAdmin />}
          {workflowToolsSubTab === 'sop-formats' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-super-dark-purple mb-2">SOP Format Templates</h2>
                <p className="text-gray-600 text-sm">
                  Client-specific SOP format templates, keyed to Client Engagement tags. When a project
                  is created under a tag with an active format (e.g. Pacific Life), every SOP generated
                  for that project follows the client-approved structure automatically — section list,
                  Controls Registry / Gaps Register locations, table layouts, and formatting conventions.
                  Tags without a format use the standard Jarvis 12-section structure.
                </p>
              </div>
              <ClientSopFormatsAdmin />
            </div>
          )}
        </div>
      )}

      {/* User Create Modal */}
      <UserCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onUserCreated={() => {
          fetchUsers();
          fetchStats();
        }}
      />

      {/* Framework Detail Modal */}
      <FrameworkDetailModal
        framework={selectedFramework}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedFramework(null);
        }}
        onLoadControls={handleLoadControls}
        onLoadCitations={handleLoadCitations}
      />

    </div>
  );
};

export default Admin;
